/**
 * @file WmsChannelReservesPage.tsx
 * @description «Резервы по каналам» — резерв и защитный буфер каждого канала
 * продаж (phase13.0; общий стиль — phase19.23, A5; phase19.31 зона «Склад» —
 * переведён целиком на Commora).
 *
 * Страницу решили оставить (решение пользователя 2026-09-16): она отвечает на
 * вопрос «сколько товара держит каждый канал». Заголовок совпадает с пунктом
 * меню, главного действия нет. Фильтры: поиск, канал, склад. Список —
 * `ResponsiveList` (таблица от 1024, компактный список до 1024).
 */

import { Search, Split } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterBar } from "../../../components/ui/FilterBar";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { SearchInput } from "../../../components/ui/SearchInput";
import { SelectField } from "../../../components/ui/SelectField";
import { CHANNEL_RESERVE_COLUMNS } from "../../../components/wms/channel-reserves-columns";
import {
  SALES_CHANNEL_LABEL,
  channelReservesApi,
  formatSkuName,
  warehousesApi,
  type ChannelReserveDto,
  type SalesChannelKind,
  type WarehouseDto,
} from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";
import { warehouseOptions } from "../../../lib/wms-registry-filters";

/** Каналы — реестром из api-wms: новый канал появляется в фильтре сам. */
const CHANNEL_OPTIONS = [
  { value: "", label: "Все каналы" },
  ...Object.entries(SALES_CHANNEL_LABEL).map(([value, label]) => ({ value, label })),
];

export function WmsChannelReservesPage() {
  const notify = useNotify();
  const [items, setItems] = useState<ChannelReserveDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [channel, setChannel] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    warehousesApi
      .list()
      .then(setWarehouses)
      .catch(() => {
        // Без справочника фильтр по складу выглядит пустым — говорим об этом.
        setWarehouses([]);
        notify.error("Не удалось загрузить склады, фильтр по складу недоступен");
      });
  }, [notify]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    channelReservesApi
      .list({ warehouseId: warehouseId || undefined, channel: (channel || undefined) as SalesChannelKind | undefined })
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить резервы. Проверьте связь и попробуйте ещё раз."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, channel, attempt]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) => formatSkuName(it.sku).toLowerCase().includes(q) || (it.sku?.code ?? "").toLowerCase().includes(q),
    );
  }, [items, search]);

  function reset() {
    setSearch("");
    setChannel("");
    setWarehouseId("");
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Резервы по каналам"
        subtitle="Резерв под заказы и защитный буфер, который каналу не показывают как доступный"
      />

      <FilterBar
        search={<SearchInput value={search} onChange={setSearch} placeholder="Товар или артикул" ariaLabel="Поиск по резервам каналов" />}
      >
        <SelectField
          value={channel}
          onChange={setChannel}
          options={CHANNEL_OPTIONS}
          placeholder="Все каналы"
          ariaLabel="Канал продаж"
          className="w-full sm:w-[200px]"
        />
        <SelectField
          value={warehouseId}
          onChange={setWarehouseId}
          options={warehouseOptions(warehouses)}
          placeholder="Все склады"
          ariaLabel="Склад"
          className="w-full sm:w-[200px]"
        />
      </FilterBar>

      <ResponsiveList
        aria-label="Резервы по каналам продаж"
        rows={visible}
        columns={CHANNEL_RESERVE_COLUMNS}
        rowKey={(it) => it.id}
        rowSize="double"
        loading={loading}
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        filtered={!!(search || warehouseId || channel)}
        empty={{
          icon: Split,
          title: "Резервов по каналам пока нет",
          description: "Здесь появятся резервы и буферы каналов продаж. Движение и заказы по каналу — в следующем обновлении.",
        }}
        filteredEmpty={{
          icon: Search,
          title: "Под эти условия резервов нет",
          description: "Измените поиск, канал или склад.",
          onReset: reset,
        }}
      />
    </div>
  );
}
